import React from "react";
import { Nav, NavItem, NavLink, TabContent, TabPane } from "reactstrap";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import { PrinterRowData, PrinterSettingTabKey } from "./types";
import { PRINTER_TICKET_TYPE } from "./constants";

const TAB_GRID_TEMPLATE = "220px 1fr";
const TAB_FONT_SIZE = "15px";

type TabConfig = {
    key: PrinterSettingTabKey;
    title: string;
    customClassName: string;
    content: React.ReactNode;
};

export type MenuWrapperProps = {
    rowData: PrinterRowData;
    tabRenderers: Record<PrinterSettingTabKey, React.ReactNode>;
};

function isCustomerTicketDisabled(rowData: PrinterRowData): boolean {
    const ticketsValue = rowData.tickets?.value;
    return (
        ticketsValue === PRINTER_TICKET_TYPE.PRODUCTION ||
        ticketsValue === PRINTER_TICKET_TYPE.STICKER ||
        ticketsValue === PRINTER_TICKET_TYPE.VOUCHER
    );
}

function isKitchenSideDisabled(rowData: PrinterRowData): boolean {
    return rowData.tickets?.value === PRINTER_TICKET_TYPE.CUSTOMER;
}

export function MenuWrapper({
    rowData,
    tabRenderers,
}: MenuWrapperProps): JSX.Element {
    const { t } = useTranslation();
    const [activeTab, setActiveTab] = React.useState<number>(0);
    const mainColor = useSelector(
        (state: { global: { infoUser: { maincolor: string } } }) =>
            state.global.infoUser.maincolor
    );

    const tabs: TabConfig[] = [
        {
            key: "general",
            title: t("General"),
            customClassName: "afa_sid_generalPopup",
            content: tabRenderers.general,
        },
        {
            key: "customerTicket",
            title: t("Customer ticket"),
            customClassName: "afa_sid_ticketClientPopup",
            content: tabRenderers.customerTicket,
        },
        {
            key: "kitchenTicket",
            title: t("Kitchen ticket"),
            customClassName: "afa_sid_kitchenTicketPopup",
            content: tabRenderers.kitchenTicket,
        },
        {
            key: "modeleTicket",
            title: t("Models tickets"),
            customClassName: "afa_sid_ticketTemplatePopup",
            content: tabRenderers.modeleTicket,
        },
        {
            key: "divers",
            title: t("Various"),
            customClassName: "afa_sid_variousPopup",
            content: tabRenderers.divers,
        },
    ];

    const customerDisabled = isCustomerTicketDisabled(rowData);
    const kitchenDisabled = isKitchenSideDisabled(rowData);

    const toggleNav = (tab: number) => {
        if (activeTab !== tab) setActiveTab(tab);
    };

    return (
        <div
            style={{
                display: "grid",
                gridTemplateColumns: TAB_GRID_TEMPLATE,
                fontSize: TAB_FONT_SIZE,
            }}
        >
            <Nav
                tabs
                className="d-flex flex-column"
                style={{ backgroundColor: mainColor, height: "100%" }}
            >
                {tabs.map((element, index) => {
                    const disabled =
                        (customerDisabled &&
                            element.key === "customerTicket") ||
                        (kitchenDisabled &&
                            (element.key === "kitchenTicket" ||
                                element.key === "modeleTicket"));

                    return (
                        <NavItem
                            key={element.key}
                            data-testid={`printer-setting-tab-${element.key}`}
                            className={classNames(
                                `pb-1 menu-wrapper-nav-link__clz ${element.customClassName}`,
                                {
                                    "active menu-wrapper-items-nav-link-active__clz":
                                        activeTab === index,
                                    "not-allowed-icon__clz":
                                        disabled &&
                                        element.key === "customerTicket",
                                    "not-allowed-customer-icon__clz":
                                        disabled &&
                                        element.key !== "customerTicket",
                                }
                            )}
                        >
                            <NavLink
                                className={classNames(
                                    "border-0 menu-wrapper-nav-link__clz",
                                    {
                                        "active menu-wrapper-items-nav-link-active__clz":
                                            activeTab === index,
                                    }
                                )}
                                onClick={() => toggleNav(index)}
                            >
                                <span className="px-2">{element.title}</span>
                            </NavLink>
                        </NavItem>
                    );
                })}
            </Nav>
            <TabContent activeTab={activeTab}>
                {tabs.map((element, index) => (
                    <TabPane
                        tabId={index}
                        key={element.key}
                        style={{
                            height: "100%",
                            width: "100%",
                            overflow: "hidden",
                        }}
                    >
                        {element.content}
                    </TabPane>
                ))}
            </TabContent>
        </div>
    );
}
